import { cn } from '@spiaggeit/spit-ui'
import { ReactNode } from 'react'

import { ChevronUp } from '@/assets/icons/ChevronUp'
import { TicketConstraintLines } from '@/components/TicketConstraintLines/TicketConstraintLines'

interface Props {
  children: ReactNode
  constraintClassName?: string
  hasError?: boolean
  hasReservationMaxError?: boolean
  hasReservationMinError?: boolean
  hasSpotMaxError?: boolean
  hasSpotMinError?: boolean
  isAccordion?: boolean
  isExpanded?: boolean
  maximumReservationQuantity?: number
  maximumSpotQuantity?: number
  minimumReservationQuantity?: number
  minimumSpotQuantity?: number
  name: string
  onToggle?: () => void
  reservationErrorAnchor?: string
  spotErrorAnchor?: string
  titleClassName?: string
}

export function ServiceGroupAccordion(props: Props) {
  const headerContent = (
    <div className="min-w-0 flex-1 text-left">
      <h3 className={props.titleClassName ?? 'font-bold text-primary'}>
        {props.name}
      </h3>
      <TicketConstraintLines
        constraintClassName={props.constraintClassName}
        constraintScope="groupReservation"
        errorAnchor={props.reservationErrorAnchor ?? 'group-reservation'}
        hasMaxError={props.hasReservationMaxError}
        hasMinError={props.hasReservationMinError}
        maximumQuantity={props.maximumReservationQuantity}
        minimumQuantity={props.minimumReservationQuantity}
      />
      <TicketConstraintLines
        constraintClassName={props.constraintClassName}
        constraintScope="groupSpot"
        errorAnchor={props.spotErrorAnchor ?? 'group-spot'}
        hasMaxError={props.hasSpotMaxError}
        hasMinError={props.hasSpotMinError}
        maximumQuantity={props.maximumSpotQuantity}
        minimumQuantity={props.minimumSpotQuantity}
      />
    </div>
  )

  const containerClassName = cn('overflow-hidden rounded border', {
    'border-neutral-200 bg-white': !props.hasError,
    'border-red-500 bg-red-25': props.hasError,
  })

  if (props.isAccordion) {
    return (
      <div className={containerClassName}>
        <button
          aria-expanded={props.isExpanded}
          className="flex w-full items-center gap-3 p-3"
          onClick={props.onToggle}
          type="button"
        >
          {headerContent}
          <ChevronUp
            className={cn('size-5 shrink-0 transition-transform', {
              'rotate-180': !props.isExpanded,
            })}
          />
        </button>

        {/* Children stay mounted when collapsed: quantity sync effects in
            ServiceQuantityController must keep running (see min/max per pieces) */}
        <div
          className={cn(
            'border-neutral-200 flex flex-col gap-3 border-t px-3 pb-3 pt-3',
            { hidden: !props.isExpanded }
          )}
        >
          {props.children}
        </div>
      </div>
    )
  }

  return (
    <div className={cn(containerClassName, 'flex flex-col gap-3 p-4')}>
      {headerContent}
      {props.children}
    </div>
  )
}
